// Records a camera check for one vehicle (date, who, notes). The newest check resets that vehicle's camera-check
// clock, so the next one comes due after the "Camera Check Every (months)" setting (or the vehicle's own override).
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { Trash2 } from "lucide-react";
import { formatDate } from "@/lib/dates";
import { localIsoDay } from "@/lib/vehicleChecks";
import {
  deleteCameraCheck,
  loadCameraChecks,
  loggedInUserName,
  recordCameraCheck,
  type CameraCheckRecord,
} from "@/lib/vehicleChecksData";

interface CameraCheckDialogProps {
  open: boolean;
  vehicleNumber: string | null;
  /** e.g. "Mini 0806 · SD / storage card · ID CAM-9" -- shown so the checker knows what they're looking at. */
  cameraSummary?: string;
  onClose: () => void;
  onSaved: () => void;
}

const CameraCheckDialog: React.FC<CameraCheckDialogProps> = ({ open, vehicleNumber, cameraSummary, onClose, onSaved }) => {
  const [checkedOn, setCheckedOn] = useState(() => localIsoDay(new Date()));
  const [checkedBy, setCheckedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<CameraCheckRecord[]>([]);

  const loadHistory = async (vehicle: string) => {
    try {
      setHistory(await loadCameraChecks(vehicle));
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    if (!open || !vehicleNumber) return;
    setCheckedOn(localIsoDay(new Date()));
    setCheckedBy(loggedInUserName());
    setNotes("");
    loadHistory(vehicleNumber);
  }, [open, vehicleNumber]);

  const save = async () => {
    if (!vehicleNumber) return;
    setSaving(true);
    try {
      await recordCameraCheck({ vehicle_number: vehicleNumber, checked_on: checkedOn, checked_by: checkedBy, notes });
      toast({ title: "Camera check recorded", description: `Vehicle ${vehicleNumber} was checked on ${formatDate(checkedOn)}.` });
      onSaved();
      onClose();
    } catch (e: any) {
      toast({ title: "Couldn't save the check", description: e?.message ?? String(e), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      await deleteCameraCheck(id);
      if (vehicleNumber) await loadHistory(vehicleNumber);
      onSaved();
    } catch (e: any) {
      toast({ title: "Couldn't remove that check", description: e?.message ?? String(e), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle>Camera check — vehicle {vehicleNumber}</DialogTitle>
          <DialogDescription>
            {cameraSummary || "Record that this vehicle's camera has been checked."} This restarts its camera-check timer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="camera-checked-on">Date checked</Label>
              <Input
                id="camera-checked-on"
                type="date"
                value={checkedOn}
                max={localIsoDay(new Date())}
                onChange={(e) => setCheckedOn(e.target.value)}
                className="dark-input mt-1"
              />
            </div>
            <div>
              <Label htmlFor="camera-checked-by">Checked by</Label>
              <Input id="camera-checked-by" value={checkedBy} onChange={(e) => setCheckedBy(e.target.value)} className="dark-input mt-1" />
            </div>
          </div>
          <div>
            <Label htmlFor="camera-notes">Notes (optional)</Label>
            <Textarea
              id="camera-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Footage reviewed, card swapped, lens cleaned"
              className="dark-input mt-1"
              rows={3}
            />
          </div>

          {history.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Previous checks</div>
              <ul className="space-y-1 max-h-36 overflow-y-auto">
                {history.map((h) => (
                  <li key={h.id} className="flex items-start justify-between gap-2 text-xs rounded border border-border/60 px-2 py-1">
                    <span>
                      <span className="font-medium text-foreground">{formatDate(h.checked_on)}</span>
                      {h.checked_by ? ` · ${h.checked_by}` : ""}
                      {h.notes ? <span className="block text-muted-foreground">{h.notes}</span> : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => remove(h.id)}
                      className="text-muted-foreground hover:text-red-400 shrink-0"
                      title="Remove this entry"
                      aria-label="Remove this entry"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !checkedOn} className="golden-button">
            {saving ? "Saving..." : "Record check"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CameraCheckDialog;
